import { useUI } from '@core/context/UIContext';
import React, { useEffect } from 'react';

/**
 * AdminPageHeader — Dark industrial header for all admin modules.
 * Replicates the Terminal's top bar aesthetic within the admin context.
 *
 * Props:
 *  - icon: Lucide icon component
 *  - title: Page title (uppercase, italic, bold)
 *  - subtitle: Optional small subtitle line
 *  - tabs: Array of { id, label, icon } for sub-navigation
 *  - activeTab: Currently active tab id
 *  - onTabChange: (id) => void
 *  - actions: ReactNode — CTA buttons rendered on the right
 */
const EMPTY_TABS = [];

const AdminPageHeader = ({
    icon: Icon,
    title,
    subtitle,
    tabs = EMPTY_TABS,
    activeTab,
    onTabChange,
    actions,
    className = '',
}) => {
    const { setPageTitle } = useUI();

    useEffect(() => {
        if (title) {
            setPageTitle(title);
        }
    }, [title, setPageTitle]);

    return (
        <div className={`hidden md:flex bg-primary w-full shrink-0 rounded-3xl md:rounded-[2rem] px-5 md:px-8 py-4 md:py-5 flex flex-col gap-3 md:gap-4 ${className}`}>

            {/* Row 1: Icon + Title + CTA - Hidden on mobile to avoid redundancy with top navbar */}
            <div className="hidden md:flex items-center justify-between gap-3 md:gap-4">
                {/* Left: Icon + Title */}
                <div className="flex items-center gap-3 md:gap-4 min-w-0">
                    {Icon && (
                        <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                            <Icon size={20} className="text-accent" strokeWidth={2.5} />
                        </div>
                    )}
                    <div className="min-w-0">
                        <h1 className="font-black uppercase tracking-tighter italic text-white text-xl md:text-2xl leading-none truncate pr-2">
                            {title}
                        </h1>
                        {subtitle && (
                            <p className="text-[9px] font-bold text-white/30 uppercase tracking-[0.2em] mt-1 leading-none truncate">
                                {subtitle}
                            </p>
                        )}
                    </div>
                </div>

                {/* Right: Actions slot */}
                {actions && (
                    <div className="flex items-center gap-3 shrink-0">
                        {actions}
                    </div>
                )}
            </div>

            {/* Row 2: Tabs (optional) */}
            {tabs.length > 0 && (
                <div className="flex items-center gap-2 overflow-x-auto"
                    style={{ scrollbarWidth: 'none' }}>
                    {tabs.map((tab) => {
                        const TabIcon = tab.icon;
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => onTabChange?.(tab.id)}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl border transition-all whitespace-nowrap text-[10px] font-black uppercase tracking-wider shrink-0 cursor-pointer ${isActive
                                    ? 'bg-accent border-accent text-primary-dark shadow-lg shadow-accent/30'
                                    : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white/80'
                                    }`}
                            >
                                {TabIcon && <TabIcon size={14} strokeWidth={2.5} />}
                                {tab.label}
                                {tab.count !== undefined && (
                                    <span className={`text-[9px] font-black px-2 py-0.5 rounded-full ${isActive ? 'bg-primary/20 text-primary' : 'bg-white/10 text-white/40'
                                        }`}>
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default React.memo(AdminPageHeader);
